import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';

import { DepositWithFolderAndStandardBloc } from '@connect-services/client/blocs/deposit-with-folder-and-standard.bloc';
import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { TitleComponent } from '@connect-services/client/components/field-intervention-title/title/title.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    DepositMetadataInformationComponent,
    MetadataInformation,
} from '@connect-services/client/pages/management/data/deposit/deposit-metadata-information/deposit-metadata-information.component';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import { DepositFolder, DepositWithFolderAndStandard } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { defaultPaginationParameters, OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DepositMetadataInformationComponent, PaginatorComponent, RessourcesModule, TitleComponent],
    selector: 'app-deposit-detail',
    styleUrl: '../project-settings-layer-detail-template/project-settings-layer-detail-template.component.scss',
    templateUrl: './deposit-detail.component.html',
})
export class DepositDetailComponent {
    protected readonly depositWithFolderAndStandardBloc = inject(DepositWithFolderAndStandardBloc);
    protected readonly layerListBloc = inject(LayerListBloc);
    protected readonly metadataInformation = computed<MetadataInformation | null>(() => {
        const deposit: DepositWithFolderAndStandard | null = this.depositWithFolderAndStandardBloc.value();

        if (!deposit?.standard?.isReferenceData) {
            return null;
        }

        const depositFolder: DepositFolder | undefined = deposit.depositFolder;

        return {
            depositFolderMetadata: depositFolder?.metadata ?? null,
            depositMetadata: deposit.metadata ?? null,
            standardMetadata: depositFolder?.standard?.metadata ?? null,
        };
    });

    private paginationParameters: PaginationParameters = {
        ...defaultPaginationParameters,
        order: OrderEnum.ASC,
        orderBy: 'name',
        take: 20,
    };
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);

    public async onPaginationChange(page: number): Promise<void> {
        this.paginationParameters = {
            ...this.paginationParameters,
            page: page,
        };

        await this.loadLayerListBloc();
    }

    protected async closeDetail(): Promise<void> {
        const path = [RoutePathEnum.PROJECTS, this.projectBloc.value()!.id, RoutePathEnum.SETTINGS];
        await this.router.navigate([
            ...path,
            {
                outlets: { detailPanel: null },
            },
        ]);
    }

    private async loadLayerListBloc(): Promise<void> {
        await this.layerListBloc.load(
            {
                deposit: this.depositWithFolderAndStandardBloc.value()!.id,
                geomFilter: 'ALL',
                project: this.projectBloc.value()!.id,
                ...this.paginationParameters,
            },
            { refresh: true },
        );
    }
}
